import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import {
  propertyShieldApi,
  PROPERTY_SHIELD_INTEGRATION_KEY
} from "api/propertyShield";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { BaseInput } from "shared/components/forms/BaseInput";
import { useFormTyped } from "shared/hooks/use-form-typed";
import { useAuthContext } from "providers/Auth";
import { getErrorMessage } from "utils/error";

const validationSchema = yup.object({
  name: yup.string().trim().required("Integration name is required")
});

export const PropertyShieldNameStep = ({
  onBack,
  closeModal
}: {
  onBack: () => void;
  closeModal: () => void;
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { refreshPartnerData } = useAuthContext();
  const form = useFormTyped<{ name: string }>({ name: "" }, validationSchema);

  const { mutate: connect, isPending } = useMutation({
    mutationKey: [PROPERTY_SHIELD_INTEGRATION_KEY, "CREATE"],
    mutationFn: () => propertyShieldApi.connect(form.values.name.trim()),
    onSuccess: async (data) => {
      queryClient.setQueryData([PROPERTY_SHIELD_INTEGRATION_KEY], data);
      // Connecting is what turns Listing Fraud Alerts on across the portal.
      await refreshPartnerData();
      toast.success("Integration added successfully!");
      closeModal();
      navigate("/settings/integration/property-shield");
    },
    onError: (err) => {
      toast.error("Error adding integration: " + getErrorMessage(err));
    }
  });

  const handleSubmit = form.onSubmit({ onSuccess: () => connect() });

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <BaseInput
        id="name"
        label="Name"
        placeholder="Integration name"
        {...form.register("name")}
        required
      />

      <div className="flex ml-auto gap-2 mt-2">
        <Button
          type="button"
          variant={ButtonVariant.BASE}
          size={ButtonSize.BASE}
          onClick={onBack}
          className="w-[125px]"
        >
          Back
        </Button>
        <Button
          type="submit"
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.BASE}
          className="w-[125px]"
          loading={isPending}
        >
          Continue
        </Button>
      </div>
    </form>
  );
};
